// Centralized API utility for making authenticated requests
import logger from './logger';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class APIClient {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get auth headers
  getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers
      }
    };

    try {
      const response = await fetch(url, config);
      
      // Handle 401 Unauthorized
      if (response.status === 401) {
        // Clear token and redirect to login
        localStorage.removeItem('authToken');
        window.location.href = '/account?mode=login';
        throw new Error('Authentication required');
      }

      const data = await response.json();
      
      if (!response.ok) {
        logger.error('API Error Response:', {
          status: response.status,
          statusText: response.statusText,
          data: data,
          url: url,
          message: data.message,
          error: data.error,
          details: data.details
        });
        throw new Error(data.message || data.error?.message || `HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      logger.error('API request failed:', error);
      throw error;
    }
  }

  // Convenience methods
  get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  // File upload method
  async uploadFile(endpoint, formData, options = {}) {
    const token = localStorage.getItem('authToken');
    const headers = { ...options.headers };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    // Don't set Content-Type for FormData - let browser set it
    delete headers['Content-Type'];
    
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      body: formData,
      headers
    });

    if (response.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/account?mode=login';
      throw new Error('Authentication required');
    }

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `Upload failed: ${response.status}`);
    }

    return data;
  }
}

// Create singleton instance
const apiClient = new APIClient();

// Export specific API methods
export const api = {
  // Auth endpoints
  auth: {
    login: (email, password, rememberMe = false) => apiClient.post('/user/login', { email, password, rememberMe }),
    register: (email, password, firstName, lastName) => apiClient.post('/user/register', { email, password, firstName, lastName }),
    profile: () => apiClient.get('/user/profile'),
    updateProfile: (data) => apiClient.put('/user/profile', data)
  },

  // User endpoints (including password recovery)
  users: {
    forgotPassword: (email) => apiClient.post('/user/forgot-password', { email }),
    resetPassword: (token, password) => apiClient.post('/user/reset-password', { token, password }),
    changePassword: (currentPassword, newPassword) => apiClient.post('/user/change-password', { currentPassword, newPassword }),
    resendVerification: (email) => apiClient.post('/user/resend-verification', { email })
  },

  // Session endpoints
  sessions: {
    create: (data) => apiClient.post('/session/create', data),
    getAll: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return apiClient.get(`/session/all${query ? `?${query}` : ''}`);
    },
    get: (sessionId) => apiClient.get(`/session/${sessionId}`),
    update: (sessionId, data) => apiClient.put(`/session/${sessionId}`, data),
    updateStatus: (sessionId, status) => apiClient.put(`/session/${sessionId}/status`, { status }),
    complete: (sessionId) => apiClient.post(`/session/${sessionId}/complete`),
    delete: (sessionId) => apiClient.delete(`/session/${sessionId}`)
  },

  // Upload endpoints
  upload: {
    single: (file, sessionId) => {
      const formData = new FormData();
      formData.append('image', file);
      const headers = sessionId ? { 'x-session-id': sessionId } : {};
      return apiClient.uploadFile('/upload/single', formData, { headers });
    },
    multiple: (files, sessionId) => {
      const formData = new FormData();
      files.forEach(file => formData.append('images', file));
      const headers = sessionId ? { 'x-session-id': sessionId } : {};
      return apiClient.uploadFile('/upload/multiple', formData, { headers });
    },
    folder: (files, sessionId) => {
      const formData = new FormData();
      files.forEach(file => formData.append('images', file));
      const headers = sessionId ? { 'x-session-id': sessionId } : {};
      return apiClient.uploadFile('/upload/folder', formData, { headers });
    },
    delete: (sessionId, filename) => apiClient.delete(`/upload/${sessionId}/${filename}`),
    clear: (sessionId) => apiClient.delete(`/upload/clear/${sessionId}`)
  },

  // Caption endpoints
  caption: {
    generate: (data) => apiClient.post('/caption/generate', data),
    regenerate: (sessionId, filename) => apiClient.post(`/caption/regenerate/${sessionId}/${filename}`),
    retry: (sessionId, failedFiles) => apiClient.post('/caption/retry', { sessionId, failedFiles }),
    update: (sessionId, filename, caption) => apiClient.put(`/caption/update/${sessionId}/${filename}`, { content: caption }),
    delete: (sessionId, filename) => apiClient.delete(`/caption/${sessionId}/${filename}`)
  },

  // Download endpoints
  download: {
    prepare: (sessionId, format) => apiClient.post(`/download/${sessionId}`, { format }),
    getUrl: (sessionId, format) => `${API_BASE_URL}/download/${sessionId}?format=${format}`
  },

  // Billing endpoints (if user is authenticated)
  billing: {
    getUsage: () => apiClient.get('/billing/usage'),
    getPlans: () => apiClient.get('/billing/plans'),
    createCheckout: (tier) => apiClient.post('/billing/create-checkout-session', { tier }),
    createTopUpCheckout: (topUpType) => apiClient.post('/billing/create-topup-checkout', { topUpType }),
    createStrictnessUpgradeSession: () => apiClient.post('/billing/create-strictness-upgrade'),
    getSubscription: () => apiClient.get('/billing/subscription'),
    cancelSubscription: () => apiClient.post('/billing/cancel')
  },

  // Admin endpoints
  admin: {
    getStats: () => apiClient.get('/admin/stats'),
    getUsers: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return apiClient.get(`/admin/users${query ? `?${query}` : ''}`);
    },
    updateUser: (userId, data) => apiClient.put(`/admin/users/${userId}`, data),
    getLogs: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return apiClient.get(`/admin/logs${query ? `?${query}` : ''}`);
    },
    getRevenue: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return apiClient.get(`/admin/revenue${query ? `?${query}` : ''}`);
    }
  },

  // Blog endpoints
  blog: {
    // Public endpoints
    getPosts: (params = {}) => {
      const query = new URLSearchParams(params).toString();
      return apiClient.get(`/blog/posts${query ? `?${query}` : ''}`);
    },
    getPost: (slug) => apiClient.get(`/blog/posts/${slug}`),
    
    // Admin endpoints
    admin: {
      getPosts: () => apiClient.get('/blog/admin/posts'),
      createPost: (data) => apiClient.post('/blog/admin/posts', data),
      updatePost: (id, data) => apiClient.put(`/blog/admin/posts/${id}`, data),
      deletePost: (id) => apiClient.delete(`/blog/admin/posts/${id}`)
    }
  },
  
  // Direct methods for custom endpoints
  get: (endpoint, options) => apiClient.get(endpoint, options),
  post: (endpoint, data) => apiClient.post(endpoint, data),
  put: (endpoint, data) => apiClient.put(endpoint, data),
  delete: (endpoint) => apiClient.delete(endpoint)
};

export default api;